import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/reducers';
import { fetchSuperAdminDashboardRequest } from '../store/decisions/actions';
import {
  Building2,
  FileText,
  CheckCircle,
  TrendingUp,
  Users,
  AlertCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
  PieChart,
  Pie,
} from 'recharts';
import './SuperAdminDashboard.css';

const SuperAdminDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const { superAdminDashboard, superAdminDashboardLoading } = useSelector(
    (state: RootState) => state.decisions
  );

  useEffect(() => {
    dispatch(fetchSuperAdminDashboardRequest());
  }, [dispatch]);

  const displayName = user?.first_name
    ? `${user.first_name.charAt(0)}. ${user.last_name || ''}`
    : user?.email || 'Super Admin';

  // KPI data
  const stats = superAdminDashboard?.stats || {
    organisations_actives: 0,
    decisions_traitees: 0,
    decisions_publiees: 0,
    taux_publication: 0,
    signalements_ouverts: 0,
  };

  // Organisations data
  const organisations = superAdminDashboard?.organisations || [];

  // Volume mensuel chart data
  const volumeMensuelData = useMemo(() => {
    const raw = superAdminDashboard?.volume_mensuel || [];
    return raw.map((item: any) => {
      const monthData: any = { mois: item.mois };
      // Dynamically add all org slugs as keys
      organisations.forEach((org) => {
        monthData[org.slug] = item[org.slug] || 0;
      });
      return monthData;
    });
  }, [superAdminDashboard, organisations]);

  // Comparatif data for bar chart
  const comparatifData = useMemo(() => {
    const raw = superAdminDashboard?.comparatif || [];
    return raw.map((item: any) => ({
      nom: item.nom,
      volume: item.volume_cumule,
      taux: item.taux_publication,
    }));
  }, [superAdminDashboard]);

  // Decisions par statut for stacked bar
  const decisionsParStatutData = useMemo(() => {
    const raw = superAdminDashboard?.decisions_par_statut || [];
    return raw.map((item: any) => ({
      nom: item.nom,
      brouillon: item.brouillon,
      a_verifier: item.a_verifier,
      valide: item.valide,
      anonymise: item.anonymise,
      publie: item.publie,
      rejete: item.rejete,
    }));
  }, [superAdminDashboard]);

  // Performance agents
  const performanceAgents = superAdminDashboard?.performance_agents || {};

  const orgColors = ['#E85D04', '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B'];

  const statutColors: Record<string, string> = {
    brouillon: '#9CA3AF',
    a_verifier: '#F59E0B',
    valide: '#10B981',
    anonymise: '#3B82F6',
    publie: '#059669',
    rejete: '#EF4444',
  };

  return (
    <div className="superadmin-dash">
      {/* Welcome banner */}
      <div className="superadmin-dash-welcome">
        <div>
          <h2>Bonjour {displayName} 👋</h2>
          <p>
            Tableau de bord super-administrateur — Vue d'ensemble de toutes les
            organisations.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="superadmin-dash-kpis">
        <div className="superadmin-kpi-card">
          <div className="superadmin-kpi-icon superadmin-kpi-icon-blue">
            <Building2 size={22} />
          </div>
          <div className="superadmin-kpi-info">
            <span className="superadmin-kpi-value">{stats.organisations_actives}</span>
            <span className="superadmin-kpi-label">Organisations actives</span>
            <span className="superadmin-kpi-sub">sur {organisations.length} total</span>
          </div>
        </div>

        <div className="superadmin-kpi-card">
          <div className="superadmin-kpi-icon superadmin-kpi-icon-orange">
            <FileText size={22} />
          </div>
          <div className="superadmin-kpi-info">
            <span className="superadmin-kpi-value">{stats.decisions_traitees}</span>
            <span className="superadmin-kpi-label">Décisions traitées</span>
            <span className="superadmin-kpi-sub">volume global</span>
          </div>
        </div>

        <div className="superadmin-kpi-card">
          <div className="superadmin-kpi-icon superadmin-kpi-icon-green">
            <CheckCircle size={22} />
          </div>
          <div className="superadmin-kpi-info">
            <span className="superadmin-kpi-value">{stats.decisions_publiees}</span>
            <span className="superadmin-kpi-label">Décisions publiées</span>
            <span className="superadmin-kpi-sub">
              {stats.taux_publication.toFixed(1)}% du total
            </span>
          </div>
        </div>

        <div className="superadmin-kpi-card">
          <div className="superadmin-kpi-icon superadmin-kpi-icon-red">
            <AlertCircle size={22} />
          </div>
          <div className="superadmin-kpi-info">
            <span className="superadmin-kpi-value">{stats.signalements_ouverts}</span>
            <span className="superadmin-kpi-label">Signalements ouverts</span>
            <span className="superadmin-kpi-sub">à traiter</span>
          </div>
        </div>
      </div>

      {/* Organisations table */}
      <div className="superadmin-dash-panel">
        <div className="superadmin-dash-panel-header">
          <div>
            <h3>Organisations</h3>
            <span className="superadmin-dash-panel-sub">
              Performance par organisation
            </span>
          </div>
        </div>
        <div className="superadmin-orgs-table-wrap">
          {organisations.length > 0 ? (
            <table className="superadmin-orgs-table">
              <thead>
                <tr>
                  <th>Organisation</th>
                  <th>Actif</th>
                  <th>Décisions</th>
                  <th>Publiées</th>
                  <th>Agents</th>
                  <th>Signalements</th>
                  <th>Activation</th>
                </tr>
              </thead>
              <tbody>
                {organisations.map((org) => (
                  <tr key={org.id}>
                    <td className="td-org-name">{org.nom}</td>
                    <td>
                      <span
                        className={`superadmin-badge ${
                          org.actif ? 'badge-success' : 'badge-inactive'
                        }`}
                      >
                        {org.actif ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="td-num">{org.decisions_total}</td>
                    <td className="td-num">{org.decisions_publiees}</td>
                    <td className="td-num">{org.agents}</td>
                    <td className="td-num">{org.signalements}</td>
                    <td className="td-date">
                      {org.date_activation
                        ? new Date(org.date_activation).toLocaleDateString('fr-FR')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="superadmin-empty">
              {superAdminDashboardLoading ? 'Chargement...' : 'Aucune organisation'}
            </div>
          )}
        </div>
      </div>

      {/* Charts row */}
      <div className="superadmin-dash-charts-row">
        {/* Volume mensuel line chart */}
        <div className="superadmin-dash-panel superadmin-dash-chart">
          <div className="superadmin-dash-panel-header">
            <div>
              <h3>Évolution mensuelle</h3>
              <span className="superadmin-dash-panel-sub">Volume par organisation</span>
            </div>
          </div>
          <div className="superadmin-chart-content">
            {volumeMensuelData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart
                  data={volumeMensuelData}
                  margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                >
                  <XAxis dataKey="mois" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  {organisations.map((org, idx) => (
                    <Line
                      key={org.slug}
                      type="monotone"
                      dataKey={org.slug}
                      name={org.nom}
                      stroke={orgColors[idx % orgColors.length]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="superadmin-chart-empty">
                {superAdminDashboardLoading ? 'Chargement...' : 'Aucune donnée'}
              </div>
            )}
          </div>
        </div>

        {/* Comparatif bar chart */}
        <div className="superadmin-dash-panel superadmin-dash-chart">
          <div className="superadmin-dash-panel-header">
            <div>
              <h3>Comparatif organisations</h3>
              <span className="superadmin-dash-panel-sub">Volume cumulé</span>
            </div>
          </div>
          <div className="superadmin-chart-content">
            {comparatifData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={comparatifData}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 10, bottom: 10 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="nom"
                    width={150}
                    tick={{ fontSize: 12, fontWeight: 600, fill: '#333' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === 'volume' ? `${value} décisions` : `${value}%`,
                      name === 'volume' ? 'Volume' : 'Taux publication',
                    ]}
                  />
                  <Bar dataKey="volume" radius={[0, 6, 6, 0]} barSize={24}>
                    {comparatifData.map((_: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={orgColors[index % orgColors.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="superadmin-chart-empty">
                {superAdminDashboardLoading ? 'Chargement...' : 'Aucune donnée'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decisions par statut stacked bar */}
      <div className="superadmin-dash-panel">
        <div className="superadmin-dash-panel-header">
          <div>
            <h3>Répartition par statut</h3>
            <span className="superadmin-dash-panel-sub">
              État des décisions par organisation
            </span>
          </div>
        </div>
        <div className="superadmin-chart-content">
          {decisionsParStatutData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={decisionsParStatutData}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis dataKey="nom" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="brouillon" name="Brouillon" stackId="a" fill={statutColors.brouillon} />
                <Bar dataKey="a_verifier" name="À vérifier" stackId="a" fill={statutColors.a_verifier} />
                <Bar dataKey="valide" name="Validé" stackId="a" fill={statutColors.valide} />
                <Bar dataKey="anonymise" name="Anonymisé" stackId="a" fill={statutColors.anonymise} />
                <Bar dataKey="publie" name="Publié" stackId="a" fill={statutColors.publie} />
                <Bar dataKey="rejete" name="Rejeté" stackId="a" fill={statutColors.rejete} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="superadmin-chart-empty">
              {superAdminDashboardLoading ? 'Chargement...' : 'Aucune donnée'}
            </div>
          )}
        </div>
      </div>

      {/* Performance agents by organisation */}
      <div className="superadmin-dash-panel">
        <div className="superadmin-dash-panel-header">
          <div>
            <h3>Performance des agents</h3>
            <span className="superadmin-dash-panel-sub">
              Top agents par organisation
            </span>
          </div>
        </div>
        <div className="superadmin-agents-grid">
          {organisations.map((org) => {
            const agents = performanceAgents[org.slug] || [];
            return (
              <div key={org.id} className="superadmin-agent-org-card">
                <h4 className="superadmin-agent-org-title">{org.nom}</h4>
                {agents.length > 0 ? (
                  <div className="superadmin-agent-list">
                    {agents.map((agent: any, idx: number) => (
                      <div key={agent.id || idx} className="superadmin-agent-item">
                        <span className="superadmin-agent-name">{agent.nom}</span>
                        <span className="superadmin-agent-count">{agent.decisions}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="superadmin-agent-empty">Aucun agent</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
